/**
 * Runtime Config for App Pages and Widget Loader
 *
 * Fetches per-tenant config from Front Door:
 * - Tenants:  /widget/tenants/{tenantPublicId}/config.json
 * - DP-Test:  /widget/dpt/{slotId}/config.json
 *
 * FAIL-CLOSED: Missing or invalid config prevents app/widget from loading.
 * No build-time fallback in production.
 */

import { isAllowedApiOrigin } from './allowed-origins';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// --- UI Config Types ---

export interface UiDisplayConfig {
  brandName?: string;
  companionName?: string;
  aboutBrandUrl?: string;
  /** Custom header title — replaces "CompanionName AI" when set (single color). */
  headerTitle?: string;
  /** Small text below the header title (e.g. "Sales and support available"). */
  headerSubtitle?: string;
  /** FAB icon style: 'avatar' (default) or 'chat-notification' (chat bubble + badge). */
  fabStyle?: 'avatar' | 'chat-notification';
  /** Whether to show the assistant avatar in chat. Default true. */
  showChatAvatar?: boolean;
}

export interface UiChatThemeConfig {
  companionBubbleColor?: string;
  userBubbleColor?: string;
}

export interface UiThemeConfig {
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  chat?: UiChatThemeConfig;
}

export interface UiConfig {
  display?: UiDisplayConfig;
  theme?: UiThemeConfig;
}

// --- Branding Config Types ---

export interface BrandingConfig {
  brandingPointer: string;
  baseUrl: string;
  logoPath: string;
  companionAvatarPath: string;
}

// --- Companion Messages Config ---

export interface CompanionMessagesConfig {
  tooltipMessage?: string;
  greetings?: string[];
  suggestions?: string[];
  /** Deterministic chips shown below the opening greeting bubble. Falls back to suggestions[0..3]. */
  openingSuggestions?: string[];
  /** Chat input disclaimer text. Empty string hides the disclaimer entirely. */
  chatDisclaimer?: string;
  idleHint?: string;
}

// --- Experiment Config Types ---

/** Branding overrides for an experiment variant (URLs resolved at publish time). */
export interface ExperimentVariantBrandingConfig {
  /** Resolved runtime URL for variant branding assets, set by publish workflow. */
  baseUrl?: string;
}

/** Partial overrides applied when a user is assigned to the experiment variant. */
export interface ExperimentVariant {
  ui?: {
    display?: Partial<UiDisplayConfig>;
    theme?: Partial<UiThemeConfig>;
  };
  branding?: ExperimentVariantBrandingConfig;
}

/** Configuration for a single A/B experiment. */
export interface ExperimentConfig {
  enabled: boolean;
  /** Percentage of users assigned to the variant (0–100). */
  splitPercent: number;
  /** Identifier for the variant assignment (e.g. "variant-b"). */
  variantId: string;
  variant: ExperimentVariant;
}

// --- Runtime Config ---

export interface RuntimeConfig {
  tenantPublicId: string;
  environment: 'dev' | 'dpt' | 'prd';
  widgetVersion: string;
  apiBaseUrl: string;
  features?: Record<string, boolean>;
  bundlePointer?: string;
  assetsBaseUrl: string;
  ui?: UiConfig;
  branding?: BrandingConfig;
  experiments?: Record<string, ExperimentConfig>;
}

/** Environments the frontend considers valid. */
const VALID_ENVIRONMENTS = new Set(['dev', 'dpt', 'prd']);

/** Semver-like pattern: major.minor.patch with optional prerelease. */
const SEMVER_PATTERN = /^\d+\.\d+\.\d+(-[\w.]+)?$/;

// ---------------------------------------------------------------------------
// Config loading
// ---------------------------------------------------------------------------

let cachedConfig: RuntimeConfig | null = null;

/**
 * Load config for a production/staging/dev tenant.
 *
 * Path: /widget/tenants/{tenantPublicId}/config.json
 *
 * @param baseUrl  CDN origin as an absolute URL (e.g. "https://widget.matcora.com").
 *                 When omitted the path is fetched relative to the current page
 *                 origin, which works when the page is served from the same CDN.
 *                 The loader passes this explicitly because it runs cross-origin
 *                 on merchant / SWA pages.
 */
export async function loadTenantConfig(
  tenantPublicId: string,
  baseUrl?: string,
): Promise<RuntimeConfig> {
  if (!tenantPublicId) throw new Error('tenantPublicId is required');
  const configPath = `/widget/tenants/${encodeURIComponent(tenantPublicId)}/config.json`;
  const url = baseUrl ? new URL(configPath, baseUrl).toString() : configPath;
  return loadConfigFromPath(url, tenantPublicId);
}

/** 2-digit slot ID pattern: "01".."99" (excludes "00"). */
const SLOT_ID_PATTERN = /^(0[1-9]|[1-9][0-9])$/;

/**
 * Load config for a DP-test slot.
 *
 * Path: /widget/dpt/{slotId}/config.json
 * slotId is "01".."99" (2-digit numeric, no "dp-test-" prefix).
 *
 * @param baseUrl  CDN origin as an absolute URL — see {@link loadTenantConfig}.
 */
export async function loadDptSlotConfig(slotId: string, baseUrl?: string): Promise<RuntimeConfig> {
  if (!SLOT_ID_PATTERN.test(slotId)) {
    throw new Error(`Invalid slotId: "${slotId}" (expected 2 digits: "01".."99")`);
  }
  const configPath = `/widget/dpt/${slotId}/config.json`;
  const url = baseUrl ? new URL(configPath, baseUrl).toString() : configPath;
  return loadConfigFromPath(url);
}

/**
 * Get the cached config. Throws if config hasn't been loaded yet.
 */
export function getConfig(): RuntimeConfig {
  if (!cachedConfig) throw new Error('RuntimeConfig not loaded — call loadTenantConfig or loadDptSlotConfig first');
  return cachedConfig;
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

async function loadConfigFromPath(
  url: string,
  expectedTenantPublicId?: string,
): Promise<RuntimeConfig> {
  if (cachedConfig) return cachedConfig;

  // Bypass browser cache — CDN still caches via Cache-Control: public, max-age=300
  const response = await fetch(url, { cache: 'no-store' });

  if (!response.ok) {
    throw new Error(`Config fetch failed: ${response.status} ${response.statusText} (${url})`);
  }

  // Detect HTML fallback responses (e.g. SWA navigationFallback serving index.html)
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const preview = (await response.clone().text()).slice(0, 80);
    throw new Error(
      `Expected JSON config but received ${contentType || 'unknown content-type'} ` +
      `(likely SWA navigationFallback or wrong origin). URL=${url} Preview: ${preview}`,
    );
  }

  const config = await response.json() as RuntimeConfig;
  validateConfig(config, expectedTenantPublicId);

  cachedConfig = config;
  return config;
}

function validateConfig(
  config: RuntimeConfig,
  expectedTenantPublicId?: string,
): void {
  // Required fields
  if (!config.tenantPublicId || !config.widgetVersion || !config.apiBaseUrl) {
    throw new Error('Config invalid: missing required fields (tenantPublicId, widgetVersion, apiBaseUrl)');
  }

  if (!config.assetsBaseUrl) {
    throw new Error('Config invalid: missing required field (assetsBaseUrl)');
  }

  // Tenant identity match (when loading by tenant ID)
  if (expectedTenantPublicId && config.tenantPublicId !== expectedTenantPublicId) {
    throw new Error(
      `Config tenantPublicId mismatch: expected ${expectedTenantPublicId}, got ${config.tenantPublicId}`,
    );
  }

  // Environment
  if (!VALID_ENVIRONMENTS.has(config.environment)) {
    throw new Error(`Config invalid: unknown environment '${config.environment}'`);
  }

  // Widget version format
  if (!SEMVER_PATTERN.test(config.widgetVersion)) {
    throw new Error(`Config invalid: widgetVersion '${config.widgetVersion}' is not semver`);
  }

  // API origin allowlist (environment-scoped)
  if (!isAllowedApiOrigin(config.apiBaseUrl, config.environment)) {
    throw new Error(`Untrusted API origin: ${config.apiBaseUrl}`);
  }

  // Experiment validation (fail-closed on malformed config)
  if (config.experiments) {
    let enabledCount = 0;
    for (const [name, exp] of Object.entries(config.experiments)) {
      if (exp.enabled) {
        enabledCount++;
        if (enabledCount > 1) {
          throw new Error(`Config invalid: multiple enabled experiments (at most one allowed in V1)`);
        }
        if (!exp.variantId) {
          throw new Error(`Config invalid: experiment '${name}' is enabled but has no variantId`);
        }
        if (typeof exp.splitPercent !== 'number' || exp.splitPercent < 0 || exp.splitPercent > 100) {
          throw new Error(`Config invalid: experiment '${name}' splitPercent must be 0-100`);
        }
        if (exp.variant?.branding?.baseUrl) {
          const brandUrl = exp.variant.branding.baseUrl;
          if (!brandUrl.startsWith('https://') && !brandUrl.startsWith('http://localhost')) {
            throw new Error(`Config invalid: experiment '${name}' variant branding baseUrl must be HTTPS`);
          }
        }
      }
    }
  }
}
